import { useState } from 'react'

import { ApolloError } from '@apollo/client'
import { ConvertMoneyDocument, ConvertMoneyMutation } from 'generated/resources/graphql'
import { debounce } from 'lodash'

import { useCallback } from 'modules/core/hooks/useCallback'
import { useMutation } from 'modules/core/hooks/useMutation'

interface Variables {
  baseCurrency?: string
  targetCurrency?: string
  date?: string
}

interface Options {
  onCompleted?: (data: { rate: number }) => void
  onError?: (error: ApolloError) => void
}

interface Return {
  fetch: (variables: Variables) => void
  loading: boolean
}

const BASE_AMOUNT = 1_000_000

const extractorFn = (data: ConvertMoneyMutation) => data.financeMutations.convertFinanceMoney

const getExchangeRate = (data: ConvertMoneyMutation) => {
  const convertedAmount = extractorFn(data).money?.cents
  return convertedAmount ? convertedAmount / BASE_AMOUNT : null
}

export const useExchangeRate = ({ onCompleted, onError }: Options): Return => {
  const [loading, setLoading] = useState<boolean>(false)
  const [convertMoney] = useMutation(ConvertMoneyDocument, {
    extractorFn,
    onError: (error) => {
      setLoading(false)
      onError?.(error)
    },
    onCompleted: (data) => {
      setLoading(false)
      const exchangeRate = getExchangeRate(data)
      if (!exchangeRate) return onError?.(new ApolloError({}))
      onCompleted?.({ rate: exchangeRate })
    },
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedConvertMoney = useCallback(debounce(convertMoney, 500), [])

  const fetch = ({ baseCurrency, targetCurrency, date }: Variables) => {
    if (!baseCurrency || !targetCurrency || !date) return
    if (baseCurrency === targetCurrency) {
      setLoading(false)
      debouncedConvertMoney.cancel()
      return onCompleted?.({ rate: 1 })
    } else {
      setLoading(true)
      debouncedConvertMoney({
        variables: {
          baseCurrency,
          targetCurrency,
          date,
          amount: BASE_AMOUNT,
        },
      })
    }
  }

  return { fetch, loading }
}
